import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ErrorMessage from "../components/ErrorMessage";
import PageLoading from "../components/PageLoading";
import { logIn, selectUserSession } from "../slices/userSession/userSessionSlice";
import { deleteAccessTokenCookie, login } from "../util/authentication";
import { clearAllReduxState } from "../util/commonUtils";
import { VALID_SWA_STATIONS } from "../util/constants";
import { loginErrorCodeToErrorMessageMap } from "../util/errorMapper";
import { NO_APP_ACCESS_WARNING_MESSAGE } from "../util/messageConstants";
import SpecialUserHomePage from "./SpecialUserHomePage";
import { techOpsLogIn } from "../slices/techOpsUserSession/techOpsUserSession";
import { loadCommonConfigData } from "../util/commonConfig";

const PingLogInRedirectPage = (props) => {
  const userSession = useSelector(selectUserSession);
  const dispatch = useDispatch();
  const [loginErrorMessage, setLoginErrorMessage] = useState("");
  const [loginErrorCode, setLoginErrorCode] = useState("");
  const [isUnrecoverableError, setIsUnrecoverableError] = useState(false);
  const [isSpecialAccessUser, setIsSpecialAccessUser] = useState(false);

  useEffect(() => {
    if (!props.isCookieSessionActive || !userSession.isActive) {
      clearAllReduxState();
      getAuthDataAndEmployeeData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const noAppAccess = async () => {
    const error = NO_APP_ACCESS_WARNING_MESSAGE;
    setIsUnrecoverableError(true);
    setLoginErrorMessage(error);
    deleteAccessTokenCookie();
  };

  const getAuthDataAndEmployeeData = async () => {
    if (props.error !== null) {
      // Error during Ping Server Redirect
      const error = loginErrorCodeToErrorMessageMap.pingServerRedirectError;
      setLoginErrorMessage(error.message);
      setLoginErrorCode(error.code);
    } else {
      if (props.authCode !== null) {
        let response = await login(props.authCode);
        if (!!response.has_special_access) {
          await loadCommonConfigData();
          setIsSpecialAccessUser(true);
        } else if (response && !response.error) {
          let userSessionPayload = response.user_session;
          if (userSessionPayload) {
            if (userSessionPayload.isActive) {
              let isEmployeeFromValidSwaStation = VALID_SWA_STATIONS().includes(userSessionPayload.activeStation);
              if (isEmployeeFromValidSwaStation) {
                await loadCommonConfigData();
                props.setCookieSessionActiveFunc();
                dispatch(logIn(userSessionPayload));
              } else {
                await noAppAccess();
              }
            } else if (userSessionPayload.techOpsActive) {
              props.setCookieSessionActiveFunc();
              dispatch(techOpsLogIn(userSessionPayload));
            } else {
              await noAppAccess();
            }
          } else {
            // Error pulling User Session Data together
            const error = loginErrorCodeToErrorMessageMap.userSessionDataPullError;
            setLoginErrorMessage(error.message);
            setLoginErrorCode(error.code);
          }
        } else {
          // Error getting Access Token with Auth Code
          if (response.error === NO_APP_ACCESS_WARNING_MESSAGE) {
            setIsUnrecoverableError(true);
            setLoginErrorMessage(response.error);
            deleteAccessTokenCookie();
          } else {
            const error = loginErrorCodeToErrorMessageMap.accessTokenError;
            setLoginErrorMessage(error.message);
            setLoginErrorCode(error.code);
          }
        }
      } else {
        // Error where url has query string params but authCode is not present, unknown query strings in the url?
        const error = loginErrorCodeToErrorMessageMap.unknownQueryStringError;
        setLoginErrorMessage(error.message);
        setLoginErrorCode(error.code);
      }
    }
  };

  return (
    <div className="App-contents" data-testid="ping-login-redirect-page">
      {isSpecialAccessUser ? (
        <SpecialUserHomePage setCookieSessionActiveFunc={props.setCookieSessionActiveFunc} />
      ) : loginErrorMessage !== "" ? (
        isUnrecoverableError ? (
          <div className="page-error">{loginErrorMessage}</div>
        ) : (
          <div>
            <ErrorMessage errorMessage={loginErrorMessage} errorCode={loginErrorCode} />
            <Box m={1}>
              <Button className="button" variant="contained" color="primary" size="large" fullWidth={true} href="/">
                <Typography variant="h5" component="h2">
                  <Box className="screen-message-text">Log In Again</Box>
                </Typography>
              </Button>
            </Box>
          </div>
        )
      ) : (
        <PageLoading />
      )}
    </div>
  );
};

PingLogInRedirectPage.propTypes = {
  authCode: PropTypes.string,
  error: PropTypes.string,
  setCookieSessionActiveFunc: PropTypes.func.isRequired,
  isCookieSessionActive: PropTypes.bool.isRequired,
};

export default PingLogInRedirectPage;
